/**
 * Confirmation Component
 *
 * A reusable component for binary decisions (YES/NO, Confirm/Cancel).
 * Eliminates LLM confusion about generating multiple options when only
 * a simple confirmation is needed. Uses the existing Button component and
 * aligns with the UI Core design system.
 */

import * as React from "react"
import { cn } from "@strivelabs/ui-core"
import { Button } from "@strivelabs/ui-core/v2"
import { contractButtonStyle } from "../../lib/contractButtonStyle"

export interface ConfirmationAction {
  /** Text shown on the button */
  label: string
  /** Value returned when this action is selected */
  value: string
  /** Button variant */
  variant?: "default" | "primary" | "outlinePrimary" | "destructive" | "outline" | "red" | "white" | "green" | "secondary" | "ghost" | "link" | "black" | "badge" | "gradient" | "ai-primary" | "ai-secondary"
  /** Button size */
  size?: "default" | "xs" | "sm" | "lg" | "icon"
  /** Disable this action */
  disabled?: boolean
}

export interface ConfirmationProps {
  /** The confirm action (required) */
  confirmAction: ConfirmationAction
  /** The cancel action (optional - if omitted, only confirm button is shown) */
  cancelAction?: ConfirmationAction
  /** Called when user selects an action */
  onAction: (action: ConfirmationAction) => void

  /** Label rendered above the actions */
  label?: string
  /** Description text under the label */
  description?: string
  /** Disable the whole component */
  disabled?: boolean

  /** Custom classes */
  className?: string
  actionsClassName?: string
  /** Optional name/id for accessibility */
  name?: string
  id?: string
}

const Confirmation = React.forwardRef<HTMLDivElement, ConfirmationProps>(
  (
    {
      confirmAction,
      cancelAction,
      onAction,
      label,
      description,
      disabled = false,
      className,
      actionsClassName,
      name: _name,
      id,
      ...props
    },
    ref
  ) => {
    const handleConfirm = React.useCallback(() => {
      if (!disabled && !confirmAction.disabled) {
        onAction(confirmAction)
      }
    }, [onAction, confirmAction, disabled])

    const handleCancel = React.useCallback(() => {
      if (!disabled && cancelAction && !cancelAction.disabled) {
        onAction(cancelAction)
      }
    }, [onAction, cancelAction, disabled])

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-end gap-3 rounded-lg border-half border-custom-border bg-ai-element p-4",
          className
        )}
        {...props}
      >
        {label && (
          <p id={id} className="flex-1 whitespace-pre-wrap text-base font-medium text-black">
            {label}
          </p>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}

        <div
          role="group"
          aria-labelledby={id}
          className={cn("flex items-center gap-2", actionsClassName)}
        >
          {cancelAction && (
            <Button
              type="button"
              {...contractButtonStyle({ variant: cancelAction.variant ?? "ai-secondary", size: cancelAction.size ?? "xs" })}
              onClick={handleCancel}
              disabled={disabled || cancelAction.disabled}
            >
              {cancelAction.label}
            </Button>
          )}
          <Button
            type="button"
            {...contractButtonStyle({ variant: confirmAction.variant ?? "ai-primary", size: confirmAction.size ?? "xs" })}
            onClick={handleConfirm}
            disabled={disabled || confirmAction.disabled}
          >
            {confirmAction.label}
          </Button>
        </div>
      </div>
    )
  }
)

Confirmation.displayName = "Confirmation"

export { Confirmation }